import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DocumentUploadSection } from "@/components/workflow/common/DocumentUploadSection";
import type { DocumentMetadata } from "@/contexts/WizardContext";
import { HelpCircle, Users } from "lucide-react";
import { SectionCard } from "@/components/workflow/common/SectionCard";
interface WorkforceFormProps {
  generalInfo: any;
  updateGeneralInfo: (updates: any) => void;
  isLocked?: boolean;
}

const fieldLabelClassName = "text-sm font-medium leading-5 text-slate-700";
const fieldInputClassName = "h-11 rounded-2xl border-slate-200 bg-slate-50 px-3 text-base text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-slate-300";

export function WorkforceForm({ generalInfo, updateGeneralInfo, isLocked }: WorkforceFormProps) {
  const header = (
    <div className="flex min-w-0 items-center gap-2">
      <Users className="h-5 w-5 flex-shrink-0 text-[#00444C]" />
      <h3 className="text-lg font-bold leading-7 text-slate-900">{"Workforce"}</h3>
      <HelpCircle className="h-4 w-4 flex-shrink-0 text-slate-600" />
    </div>
  );

  return (
    <SectionCard header={header}>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {"Total Employees (FTE)"} <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            placeholder={"e.g. 15"}
            value={generalInfo.totalEmployees}
            onChange={(e) => updateGeneralInfo({ totalEmployees: e.target.value })}
            data-testid="input-total-employees"
            disabled={isLocked}
            className={fieldInputClassName}
          />
        </div>
        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {"Male Employees"} <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            placeholder={"e.g. 8"}
            value={generalInfo.maleEmployees}
            onChange={(e) => updateGeneralInfo({ maleEmployees: e.target.value })}
            data-testid="input-male-employees"
            disabled={isLocked}
            className={fieldInputClassName}
          />
        </div>
        <div className="space-y-2">
          <Label className={fieldLabelClassName}>
            {"Female Employees"} <span className="text-red-500">*</span>
          </Label>
          <Input
            type="number"
            placeholder={"e.g. 7"}
            value={generalInfo.femaleEmployees}
            onChange={(e) => updateGeneralInfo({ femaleEmployees: e.target.value })}
            data-testid="input-female-employees"
            disabled={isLocked}
            className={fieldInputClassName}
          />
        </div>
        <div className="space-y-2 md:col-span-3">
          <Label className={fieldLabelClassName}>{"% Homeworking"}</Label>
          <Input
            type="number"
            placeholder={"e.g., 25"}
            min="0"
            max="100"
            value={generalInfo.homeworkingPercent}
            onChange={(e) => updateGeneralInfo({ homeworkingPercent: e.target.value })}
            data-testid="input-homeworking-percent"
            disabled={isLocked}
            className={fieldInputClassName}
          />
          <p className="mt-1 text-xs text-muted-foreground">Enter the percentage of working time that employees are allowed to work from home under your company policy (0-100%).</p>
        </div>
      </div>
      <div className="pt-4">
        <DocumentUploadSection
          fieldIdentifier="general-employee-list"
          documents={generalInfo.employeeListDocuments || []}
          onDocumentsChange={(docs: DocumentMetadata[]) => updateGeneralInfo({ employeeListDocuments: docs })}
          currentTab="general"
          label={"Employee List (optional)"}
          description={"Optionally upload an employee list or HR records to support the workforce figures above"}
          accept=".csv,.xlsx,.xls,.pdf"
          maxFiles={5}
          className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
          itemClassName="rounded-2xl border border-slate-200 bg-white p-3"
          actionButtonClassName="h-9 rounded-2xl border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-none hover:bg-slate-100"
        />
      </div>
    </SectionCard>
  );
}
